import { computed, inject, Injectable } from '@angular/core';
import { CheckinsRole, checkinsRolesFromClaims, satisfiesCheckinsRole } from './checkins-roles';
import { SESSION_PROVIDER } from './session-provider';

/**
 * The current user's Checkins access, derived from the session's role claims. Exposes cumulative
 * signals (a SuperAdmin is also an Admin and a User) for templates and guards. UI hints only — the
 * API enforces the same rules and is the real authority.
 */
@Injectable({ providedIn: 'root' })
export class CheckinsAccessService {
  private readonly session = inject(SESSION_PROVIDER);

  private readonly roles = computed(() => checkinsRolesFromClaims(this.session.roles()));

  /** Can run registration and check-in (User or higher). */
  readonly isUser = computed(() => satisfiesCheckinsRole(this.roles(), 'User'));

  /** Can manage events — create, edit, delete (Admin or higher). */
  readonly isAdmin = computed(() => satisfiesCheckinsRole(this.roles(), 'Admin'));

  /** Full control (SuperAdmin). */
  readonly isSuperAdmin = computed(() => satisfiesCheckinsRole(this.roles(), 'SuperAdmin'));

  /** True when the current user holds <paramref name="role"/> or higher. Reactive to role changes. */
  satisfies(role: CheckinsRole): boolean {
    return satisfiesCheckinsRole(this.roles(), role);
  }
}
